import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { CheckCheck, Undo2 } from 'lucide-react'
import { conferenceActionFor, formatDateBr, INBOX_STATES, priorityLabel, publicationPlainText } from '@/lib/publication-inbox'
import type { InboxItem } from '@/services/publication-inbox-service'
import { PublicationDeadlinePanel } from '@/components/deadlines/PublicationDeadlinePanel'
import { useTaskDrawer } from '@/contexts/TaskDrawerContext'
import { prefillFromPublication } from '../task-prefill'

interface InboxSidePanelProps {
  item: InboxItem | null
  onClose: () => void
  onOpenFull: (item: InboxItem) => void
  /** Conferir (Nova/Em triagem) ou desfazer (Conferidas) com um clique. */
  onConference?: (item: InboxItem, action: 'conferir' | 'desconferir') => void
  conferenceBusy?: boolean
}

/** Painel lateral com o resumo da publicação; "Abrir ficha completa" troca por uma gaveta larga (PublicationFullSheet). */
export function InboxSidePanel({ item, onClose, onOpenFull, onConference, conferenceBusy }: InboxSidePanelProps) {
  const stateLabel = INBOX_STATES.find((s) => s.value === item?.estado)?.label
  const conference = item && onConference ? conferenceActionFor(item.estado) : null
  const taskDrawer = useTaskDrawer()

  // A gaveta de tarefa também é lateral: fecha este painel e abre a tarefa pré-preenchida.
  const createTask = async (current: InboxItem) => {
    const prefill = await prefillFromPublication({
      id: current.id,
      tipoComunicacao: current.tipoComunicacao,
      numeroProcessoComMascara: current.numeroProcesso,
      siglaTribunal: current.siglaTribunal,
      nomeOrgao: current.nomeOrgao,
      texto: current.resumo,
      linkedCaseId: current.caso?.id ?? null,
    })
    onClose()
    taskDrawer.newTask(prefill)
  }
  return (
    <Sheet open={!!item} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-lg">
        {item && (
          <>
            <SheetHeader>
              <SheetTitle className="tabular-nums">{item.numeroProcesso}</SheetTitle>
              <SheetDescription>
                {item.siglaTribunal} · {item.nomeOrgao} · {formatDateBr(item.dataDisponibilizacao)}
              </SheetDescription>
            </SheetHeader>
            <div className="flex flex-col gap-4 px-4 pb-6">
              <div className="flex flex-wrap gap-1.5">
                {stateLabel && <Badge>{item.estado === 'CONFERIDA' ? 'Conferida' : stateLabel}</Badge>}
                <Badge variant="outline">{item.tipoComunicacao}</Badge>
                {item.prioridade && <Badge variant="secondary">Prioridade {priorityLabel(item.prioridade)}</Badge>}
              </div>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                <dt className="text-muted-foreground">Advogados</dt>
                <dd>{item.advogados.length ? item.advogados.map((a) => `${a.nome} (OAB/${a.ufOab} ${a.numeroOab})`).join('; ') : '—'}</dd>
                <dt className="text-muted-foreground">Responsável</dt>
                <dd>{item.responsavel?.nome ?? 'Sem responsável'}</dd>
                <dt className="text-muted-foreground">Processo</dt>
                <dd>{item.caso ? item.caso.numeroInterno ?? 'Vinculado' : 'Não vinculado'}</dd>
              </dl>
              <PublicationDeadlinePanel publication={item} />
              <p className="whitespace-pre-line text-sm leading-relaxed">{publicationPlainText(item.resumo)}{item.resumo.length >= 400 ? '…' : ''}</p>
              <div className="flex flex-wrap gap-2">
                {conference === 'conferir' && (
                  <Button disabled={conferenceBusy} onClick={() => onConference?.(item, 'conferir')}>
                    <CheckCheck aria-hidden="true" /> Conferida
                  </Button>
                )}
                {conference === 'desconferir' && (
                  <Button variant="outline" disabled={conferenceBusy} onClick={() => onConference?.(item, 'desconferir')}>
                    <Undo2 aria-hidden="true" /> Desfazer
                  </Button>
                )}
                <Button variant={conference === 'conferir' ? 'outline' : 'default'} onClick={() => onOpenFull(item)}>Abrir ficha completa</Button>
                <Button variant="outline" onClick={() => createTask(item)}>Criar tarefa</Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
